import * as React from 'react'

import mapboxgl from 'mapbox-gl'
import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import SubmitButton from '~/client/src/desktop/views/ProjectSetUp/components/SubmitButton'
import ProjectSetUpPageStore, {
  ProjectDetailsTabs,
} from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPage.store'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { HGap } from '~/client/src/shared/components/Layout'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NOOP } from '~/client/src/shared/utils/noop'
import { customDebounce } from '~/client/src/shared/utils/util'

import { Header } from '../SetupPageLayout/SetupPageLayout'
import CalendarLinkInput from './components/CalendarLinkInput/CalendarLinkInput'
import CalendarLinkInputStore from './components/CalendarLinkInput/CalendarLinkInput.store'
import ClosuresDatePicker from './components/ClosuresDatePicker'
import ClosuresDatePickerStore from './components/ClosuresDatePicker.store'
import DomainsAndSignUp from './components/DomainsAndSignUp'
import { getMapBoxPlaces } from './components/MapBoxView'
import ProjectDetails from './components/ProjectDetails'
import ProjectHeaderBar from './components/ProjectHeaderBar'
import ProjectQRCodesSettings from './components/ProjectQRCodesSettings'
import ProjectTimeSettings from './components/ProjectTimeSettings/ProjectTimeSettings'
import ProjectTypeSetting from './components/ProjectTypeSettings/ProjectTypeSetting'
import ProjectSettingsStore from './ProjectSettings.store'

import './ProjectWorkspaceSetUp.scss'

const SUBMIT_BUTTON_WIDTH = 160

interface IProjectWorkspaceSetUp {
  projectSetUpPageStore: ProjectSetUpPageStore
  projectsStore?: ProjectsStore
  projectDateStore?: ProjectDateStore
  eventsStore?: DesktopEventStore
  initProjectId: string

  isProjectCreationAvailable: boolean
  isTrackerToggleAvailable: boolean
  isDeliveriesToggleAvailable: boolean
  isLogisticsToggleAvailable: boolean
  isMaterialsToggleAvailable: boolean
  isMaterialResourcesToggleAvailable: boolean
  isFormsToggleAvailable: boolean
  isPhotosAvailable: boolean
  isAnalyticsAvailable: boolean
  isEquipmentToggleAvailable: boolean
  isStruxTagsAvailable: boolean
  isLocationsToggleAvailable: boolean

  userProjectsStore?: UserProjectsStore
  graphExecutorStore?: GraphExecutorStore
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
}

@inject(
  'projectsStore',
  'eventsStore',
  'projectDateStore',
  'graphExecutorStore',
)
@observer
export default class ProjectWorkspaceSetUp extends React.Component<IProjectWorkspaceSetUp> {
  @observable private localProps: IProjectWorkspaceSetUp = null
  public sectionsToggleState = observable(new Map<string, boolean>())

  private readonly calendarLinkInputStore: CalendarLinkInputStore = null
  private readonly store: ProjectSettingsStore
  private readonly datePickerStore: ClosuresDatePickerStore

  private readonly clearPostEventCallback: () => void = NOOP
  public constructor(props: IProjectWorkspaceSetUp) {
    super(props)

    this.localProps = props

    this.datePickerStore = new ClosuresDatePickerStore(
      this.props.projectDateStore,
    )

    this.store = new ProjectSettingsStore(
      props.projectsStore,
      props.eventsStore,
      props.projectSetUpPageStore,
      props.graphExecutorStore,
      props.projectDateStore,
    )

    this.mapBoxOnQueryChange = customDebounce(this.mapBoxOnQueryChange, 500)

    this.clearPostEventCallback = props.eventsStore.addPostEventCallback(
      this.store.onProjectUpdate,
    )
    this.calendarLinkInputStore = new CalendarLinkInputStore(
      this.props.graphExecutorStore,
      this.props.projectDateStore,
      this.props.eventsStore.appState.activeProject.id,
      this.props.eventsStore.appState.activeProject.code,
    )
    makeObservable(this)
  }

  public componentDidMount() {
    this.calendarLinkInputStore.initialize()
    this.store.setProjectDataValues()
  }

  public componentDidUpdate(prevProps: Readonly<IProjectWorkspaceSetUp>) {
    if (
      prevProps.initProjectId !== this.props.initProjectId ||
      !this.store.projectAddress
    ) {
      this.store.setProjectDataValues()
    }
  }

  public componentWillUnmount() {
    this.clearPostEventCallback()
  }

  public render() {
    const { isNewProjectSelected } = this.store

    return (
      <div className="relative project-details full-height">
        <div
          className={classList({
            'delivery-workflow-date-picker-container': true,
            shown: this.datePickerStore.isShown,
          })}
        >
          <ClosuresDatePicker
            store={this.datePickerStore}
            className="date-closures-picker delivery-workflow-date-picker ma-auto sticky tm-dp-container col x-stretch y-start"
          >
            {this.renderDateInputs()}
          </ClosuresDatePicker>
        </div>
        {this.renderConfirmationModal()}
        <Header
          title={Localization.translator.projectSettings}
          className="row"
        />
        <ProjectHeaderBar store={this.store} />
        <div className="project-details-tabs-content">
          {this.renderTabContent()}
        </div>

        {isNewProjectSelected && (
          <div className="setup-form-footer">
            <SubmitButton
              text={Localization.translator.save}
              width={SUBMIT_BUTTON_WIDTH}
              clickHandler={this.handleClick}
            />
          </div>
        )}
      </div>
    )
  }

  private renderDomainsAndSignUp = (): JSX.Element => {
    return <DomainsAndSignUp store={this.store} />
  }

  private renderTabContent() {
    const {
      isDeliveriesToggleAvailable,
      isLogisticsToggleAvailable,
      isMaterialsToggleAvailable,
      isMaterialResourcesToggleAvailable,
      isPhotosAvailable,
      isFormsToggleAvailable,
      isTrackerToggleAvailable,
      isAnalyticsAvailable,
      isEquipmentToggleAvailable,
      isStruxTagsAvailable,
      isLocationsToggleAvailable,
    } = this.props
    const { isNewProjectSelected, activeTab } = this.store
    const { isWorkflowsDisabled, isLogisticsDisabled } =
      this.props.eventsStore.appState
    const shouldRenderQRCodes =
      (!isWorkflowsDisabled || !isLogisticsDisabled) && !isNewProjectSelected

    switch (activeTab) {
      case ProjectDetailsTabs.Details:
        return (
          <ProjectDetails
            store={this.store}
            mapBoxOnQueryChange={this.mapBoxOnQueryChange}
            isProjectCreationAvailable={this.props.isProjectCreationAvailable}
            projectSetUpPageStore={this.props.projectSetUpPageStore}
          />
        )
      case ProjectDetailsTabs.Time:
        return (
          <ProjectTimeSettings
            store={this.store}
            datePickerStore={this.datePickerStore}
          />
        )
      case ProjectDetailsTabs.SharedWorkflows:
        return <CalendarLinkInput store={this.calendarLinkInputStore} />
      case ProjectDetailsTabs.Apps:
        return (
          !isNewProjectSelected && (
            <ProjectTypeSetting
              store={this.store}
              isDeliveriesToggleAvailable={isDeliveriesToggleAvailable}
              isLogisticsToggleAvailable={isLogisticsToggleAvailable}
              isMaterialsToggleAvailable={isMaterialsToggleAvailable}
              isMaterialResourcesToggleAvailable={
                isMaterialResourcesToggleAvailable
              }
              isPhotosAvailable={isPhotosAvailable}
              isFormsToggleAvailable={isFormsToggleAvailable}
              isTrackerToggleAvailable={isTrackerToggleAvailable}
              isAnalyticsAvailable={isAnalyticsAvailable}
              isEquipmentToggleAvailable={isEquipmentToggleAvailable}
              isStruxTagsAvailable={isStruxTagsAvailable}
              isLocationsToggleAvailable={isLocationsToggleAvailable}
            />
          )
        )
      case ProjectDetailsTabs.DomainsAndSignUp:
        return !isNewProjectSelected && this.renderDomainsAndSignUp()
      case ProjectDetailsTabs.QRCodes:
        return (
          shouldRenderQRCodes && <ProjectQRCodesSettings store={this.store} />
        )
    }
  }

  private mapBoxOnQueryChange = async (query: string): Promise<void> => {
    const data: GeoJSON.FeatureCollection = await getMapBoxPlaces(
      query,
      mapboxgl.accessToken,
      this.store.projectAddress?.center,
    )

    this.store.setProjectAddressSuggestions(
      data.features ? (data.features as IMapBoxFeature[]) : [],
    )
  }

  private renderDateInputs() {
    const { getDashedFormattedDate } = this.props.projectDateStore
    const { startDate, endDate } = this.datePickerStore

    return (
      <div className="row pb20 y-start">
        <StruxhubInput
          type="date"
          id="start-date"
          value={startDate ? getDashedFormattedDate(startDate) : ''}
          isReadOnly={true}
          isRequiredTextHidden={true}
          onChange={undefined}
          label={Localization.translator.closureStartDate}
        />
        <HGap size="70" />
        <StruxhubInput
          type="date"
          id="end-date"
          value={endDate ? getDashedFormattedDate(endDate) : ''}
          isReadOnly={true}
          isRequiredTextHidden={true}
          onChange={undefined}
          label={Localization.translator.closureEndDate}
        />
      </div>
    )
  }

  private renderConfirmationModal() {
    const {
      isConfirmationModalDisplayed,
      toggleConfirmationModal,
      confirmTimzoneChange,
    } = this.store

    return (
      <ConfirmDialog
        isOpen={isConfirmationModalDisplayed}
        onCancelClicked={toggleConfirmationModal}
        onDoneClicked={confirmTimzoneChange}
        title={Localization.translator.warning}
        doneTitle={Localization.translator.proceed}
      >
        <div className="tw:text-body-md tw:text-foreground">
          {
            Localization.translator.projectDetailsDescriptions
              .changeProjectLocationWarning
          }
        </div>
      </ConfirmDialog>
    )
  }

  private handleClick = () => {
    if (!this.store.isProjectErrorMessageVisible) {
      this.store.submitForm()
    }
  }
}
